import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  Award,
  Calendar,
  CheckCircle,
  Clock,
  Monitor,
  Sparkles,
  Linkedin,
} from 'lucide-react';
import ProgramHighlightsAccordion from '../../components/course/ProgramHighlightsAccordion';
import { API_PATHS, apiHeaders, apiUrl } from '../../config/api';
import { COURSE_IMAGES } from '../../config/courseImages';
import { HOME_CONTENT } from '../../content/homePage';
import { PROGRAM_SERIES } from '../../content/programSeries';
import { Course, CourseResponse } from '../../types';
import Button from '../../components/UI/Button';
import HeroBackdrop from '../../components/UI/HeroBackdrop';
import { heroCtaBand, heroImageFade, heroShell } from '../../content/heroThemes';
import CourseFee from '../../components/UI/CourseFee';
import { useAuth } from '../../context/AuthContext';
import { getCourseFeeDisplay } from '../../utils/currency';
import { fetchActiveCourses, findFlagshipCourse } from '../../utils/courses';
import { formatDate } from '../../utils/formatDate';
import { parseProgramContent, parseStringList } from '../../utils/parseCourseContent';
import { getUserProfileId } from '../../utils/userProfile';
import { mentorApi } from '../../services/api';
import ProfileAvatar from '../../components/UI/ProfileAvatar';

type ProgramMentorCard = {
  mentorId: number;
  mentorName?: string | null;
  jobTitle?: string | null;
  functionalDomain?: string | null;
  yearsOfExperience?: number;
  primaryExpertiseArea?: string | null;
  linkedInUrl?: string | null;
  profileImageUrl?: string | null;
};

function mapProgramMentors(rows: Array<Record<string, unknown>>): ProgramMentorCard[] {
  return (rows || []).map((row) => ({
    mentorId: Number(row.mentorId ?? row.MentorId),
    mentorName: (row.mentorName ?? row.MentorName) as string | null,
    jobTitle: (row.jobTitle ?? row.JobTitle) as string | null,
    functionalDomain: (row.functionalDomain ?? row.FunctionalDomain) as string | null,
    yearsOfExperience: Number(row.yearsOfExperience ?? row.YearsOfExperience ?? 0),
    primaryExpertiseArea: (row.primaryExpertiseArea ?? row.PrimaryExpertiseArea) as string | null,
    linkedInUrl: (row.linkedInUrl ?? row.LinkedInUrl) as string | null,
    profileImageUrl: (row.profileImageUrl ?? row.ProfileImageUrl) as string | null,
  }));
}

function renderOverview(text: string) {
  return text.split('**').map((part, index) =>
    index % 2 === 0 ? (
      <span key={index}>{part}</span>
    ) : (
      <strong key={index} className="font-semibold text-gray-900">
        {part}
      </strong>
    )
  );
}

const ProgramDetail: React.FC = () => {
  const { programId } = useParams<{ programId?: string }>();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollStatus, setEnrollStatus] = useState<string | null>(null);
  /** null = enrollment check still in flight for the signed-in user */
  const [enrolledCourseIds, setEnrolledCourseIds] = useState<number[] | null>(null);
  const [programMentors, setProgramMentors] = useState<ProgramMentorCard[]>([]);

  const { user, token, isAuthenticated, isProfileComplete, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchCourse = async () => {
      setLoading(true);
      setError(null);
      setEnrollStatus(null);
      try {
        const courses = await fetchActiveCourses();
        const numericId = programId && /^\d+$/.test(programId) ? Number(programId) : null;
        if (numericId) {
          const response = await fetch(apiUrl(API_PATHS.courses.byId(numericId)), {
            headers: apiHeaders(),
          });
          const data: CourseResponse = await response.json();
          if (data.success && data.course) {
            setCourse(data.course);
            mentorApi.programMentors(data.course.id)
              .then((r) => setProgramMentors(mapProgramMentors((r.data.mentors || []) as Array<Record<string, unknown>>)))
              .catch(() => setProgramMentors([]));
          } else {
            setCourse(null);
            setError(data.message || 'Program is not available at the moment.');
          }
        } else if (programId === 'ai-engineer' || !programId) {
          const flagship = findFlagshipCourse(courses);
          if (flagship) {
            setCourse(flagship);
            mentorApi.programMentors(flagship.id)
              .then((r) => setProgramMentors(mapProgramMentors((r.data.mentors || []) as Array<Record<string, unknown>>)))
              .catch(() => setProgramMentors([]));
          } else {
            setCourse(null);
            setError('The Flagship program is not available at the moment.');
          }
        } else {
          setCourse(null);
          setError('Program is not available at the moment.');
        }
      } catch {
        setCourse(null);
        setError('Unable to load program details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, [programId]);

  // Fetch enrollments as soon as the user is known — in parallel with the course
  // request — so apply actions don't wait on a second sequential round-trip.
  useEffect(() => {
    if (authLoading) {
      return;
    }

    const profileId = getUserProfileId(user);
    if (!isAuthenticated || !profileId) {
      setEnrolledCourseIds([]);
      return;
    }

    let cancelled = false;

    const fetchEnrollments = async () => {
      try {
        const response = await fetch(apiUrl(API_PATHS.enrollments.byUser(profileId)), {
          headers: apiHeaders(token || localStorage.getItem('token')),
        });
        const data = await response.json();
        if (!cancelled) {
          const ids = (data.enrolledCourses || []).map(
            (enrollment: { courseId: number }) => enrollment.courseId
          );
          setEnrolledCourseIds(ids);
        }
      } catch {
        if (!cancelled) {
          setEnrolledCourseIds([]);
        }
      }
    };

    fetchEnrollments();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, user?.id, token]);

  const handleEnroll = async () => {
    if (!isAuthenticated) {
      navigate('/login', {
        state: {
          message: `Please sign in to apply for ${course?.title ?? 'this program'}.`,
          from: location.pathname,
        },
      });
      return;
    }
    const profileId = getUserProfileId(user);
    if (!profileId || !course) return;

    if (!isProfileComplete) {
      setEnrollStatus('Please complete your profile before applying.');
      navigate('/profile-setup');
      return;
    }

    if (!user.isEmailVerified) {
      setEnrollStatus('Please verify your email before applying.');
      navigate('/verify-email');
      return;
    }

    setEnrolling(true);
    setEnrollStatus(null);
    try {
      const response = await fetch(apiUrl(API_PATHS.enrollments.enroll), {
        method: 'POST',
        headers: apiHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ courseId: course.id, userProfileId: profileId }),
      });
      const data = await response.json();
      if (response.ok) {
        setEnrollStatus(null);
        setEnrolledCourseIds((prev) =>
          prev ? [...new Set([...prev, course.id])] : [course.id]
        );
      } else {
        setEnrollStatus(data.message || 'Application could not be submitted.');
      }
    } catch {
      setEnrollStatus('Application could not be submitted. Please try again.');
    } finally {
      setEnrolling(false);
    }
  };

  const renderApplyActions = (opts?: {
    fullWidth?: boolean;
    variant?: 'primary' | 'onDark';
  }) => {
    const fullWidth = opts?.fullWidth ?? false;
    const variant = opts?.variant ?? 'primary';
    if (!course) return null;

    const checkingEnrollment =
      isAuthenticated && enrolledCourseIds === null;
    const isEnrolled =
      enrolledCourseIds !== null && enrolledCourseIds.includes(course.id);

    // Known enrolled state — only after the background check has finished.
    if (!checkingEnrollment && isEnrolled) {
      const submittedTextClass =
        variant === 'onDark' ? 'text-emerald-200' : 'text-emerald-600';
      return (
        <div className="space-y-3">
          <div className={`flex items-start justify-center gap-2 text-sm font-medium text-center ${submittedTextClass}`}>
            <CheckCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              Application submitted — track status in Dashboard or check your email.
            </span>
          </div>
          <Link to="/dashboard?tab=academy" className="block">
            <Button variant={variant} fullWidth={fullWidth}>
              Go to dashboard
            </Button>
          </Link>
        </div>
      );
    }

    if (authLoading) {
      return (
        <Button variant={variant} fullWidth={fullWidth} size="lg" disabled>
          Apply now
        </Button>
      );
    }

    if (isAuthenticated && !isProfileComplete) {
      const profileHintClass =
        variant === 'onDark' ? 'text-amber-100' : 'text-amber-700';
      return (
        <div className="space-y-2">
          <p className={`text-sm ${profileHintClass}`}>
            Complete your profile before you can apply.
          </p>
          <Link to="/profile-setup" className={fullWidth ? 'block' : 'inline-block'}>
            <Button variant={variant} fullWidth={fullWidth} size="lg">
              Complete profile to apply
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      );
    }

    if (isAuthenticated && !user?.isEmailVerified) {
      const verifyHintClass =
        variant === 'onDark' ? 'text-amber-100' : 'text-amber-700';
      return (
        <div className="space-y-2">
          <p className={`text-sm ${verifyHintClass}`}>
            Verify your email before you can apply.
          </p>
          <Link to="/verify-email" className={fullWidth ? 'block' : 'inline-block'}>
            <Button variant={variant} fullWidth={fullWidth} size="lg">
              Verify email to apply
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      );
    }

    if (isAuthenticated) {
      return (
        <Button
          variant={variant}
          fullWidth={fullWidth}
          size="lg"
          onClick={handleEnroll}
          disabled={enrolling || checkingEnrollment}
        >
          {enrolling ? 'Submitting…' : 'Apply now'}
          {!enrolling && <ArrowRight className="ml-2 h-5 w-5" />}
        </Button>
      );
    }

    return (
      <Link to="/login" state={{ from: location.pathname }} className={fullWidth ? 'block' : 'inline-block'}>
        <Button variant={variant} fullWidth={fullWidth} size="lg">
          Sign in to apply
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </Link>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <p className="text-gray-600">{error || 'Program not found.'}</p>
        <Link to="/programs" className="mt-6 inline-flex">
          <Button>
            Browse IDC Academy programs
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </Link>
      </div>
    );
  }

  const program = parseProgramContent(course.content);
  const materials = parseStringList(course.courseMaterials);
  const tools = parseStringList(course.tools);
  const feeDisplay = getCourseFeeDisplay(course.fee, course.discount);

  const isEnrolledForCourse =
    enrolledCourseIds !== null && enrolledCourseIds.includes(course.id);

  const stats = [
    { icon: Monitor, label: 'Mode', value: course.trainingMode },
    { icon: Clock, label: 'Duration', value: course.duration },
    { icon: Calendar, label: 'Starts', value: formatDate(course.startDate) },
    { icon: Award, label: 'Program fee', value: null as string | null },
  ];

  return (
    <div>
      {/* ===== HERO ===== */}
      <section className={heroShell('academy')}>
        <HeroBackdrop tone="academy" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[1.35rem] md:py-[1.575rem] lg:py-[1.8rem]">
          <div className="grid lg:grid-cols-2 gap-[1.125rem] lg:gap-[1.8rem] items-center">
            {/* Copy */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-200">
                {PROGRAM_SERIES.navPrograms}
              </p>
              <p className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
                {HOME_CONTENT.tagline}
              </p>
              <h1 className="mt-4 text-base md:text-lg lg:text-[1.305rem] font-semibold leading-tight text-white">
                {course.display_Title || course.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                {renderApplyActions({ variant: 'onDark' })}
                <a
                  href="#course-details"
                  className="inline-flex items-center gap-1 text-xs font-medium text-white/90 hover:text-white transition-colors"
                >
                  Explore the course
                  <ArrowRight className="h-3 w-3" />
                </a>
              </div>
              {enrollStatus && !isEnrolledForCourse && (
                <p className="mt-2 text-xs text-primary-100">{enrollStatus}</p>
              )}
            </div>

            {/* Visual */}
            <div className="relative">
              <div className="relative overflow-hidden rounded-xl shadow-2xl ring-1 ring-white/10">
                <img
                  src={COURSE_IMAGES.programHero}
                  alt="Engineers collaborating on production software"
                  className="w-full h-[12.6rem] sm:h-[15.3rem] lg:h-[18rem] object-cover"
                />
                <div className={heroImageFade('academy')} />
              </div>

              {/* Floating fee card */}
              <div className="absolute -bottom-3 left-3 right-3 sm:left-auto sm:right-4 sm:w-[11.34rem] rounded-lg bg-white text-gray-900 shadow-xl ring-1 ring-black/5 p-[0.675rem]">
                <p className="text-[0.62rem] uppercase tracking-wide text-gray-500 font-medium">
                  Program fee
                </p>
                <div className="mt-0.5">
                  <CourseFee
                    fee={course.fee}
                    discount={course.discount}
                    layout="stacked"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== STATS STRIP ===== */}
      <section className="section-band">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-5 mt-5 sm:mt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat) => (
              <div key={stat.label} className="flex items-center gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <stat.icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-500">{stat.label}</p>
                  {stat.label === 'Program fee' ? (
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      <CourseFee
                        fee={course.fee}
                        discount={course.discount}
                        showPercent={feeDisplay.hasDiscount}
                      />
                    </p>
                  ) : (
                    <p className="text-sm font-semibold text-gray-900 truncate">{stat.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ABOUT + HIGHLIGHTS ===== */}
      <section id="course-details" className="section-band">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-7">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-primary-600">
              Course details
            </span>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold text-gray-900">
              {course.title}
            </h2>
            <p className="mt-4 text-base text-gray-600 leading-relaxed text-justify">
              {renderOverview(course.courseOverview)}
            </p>
          </div>

          {course.prerequisites ? (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-900">Prerequisites</h3>
              <p className="mt-3 text-base text-gray-600 leading-relaxed">
                {course.prerequisites}
              </p>
            </div>
          ) : null}

          {program?.learningJourney.length ? (
            <div className="mt-10">
              <h3 className="text-lg font-semibold text-gray-900">How this program works</h3>
              <p className="mt-2 text-sm font-semibold text-primary-800">
                {program.learningJourney.map((step) => step.title).join(' → ')}
              </p>
              <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {program.learningJourney.map((step) => (
                  <div key={step.title} className="surface-card p-5">
                    <h4 className="font-bold text-gray-900">{step.title}</h4>
                    {step.description ? (
                      <p className="mt-2 text-sm text-gray-600 leading-relaxed">{step.description}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {program?.highlights.length ? (
            <div className="mt-10">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary-600" />
                  Program highlights
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  Explore what you will learn across {program.highlights.length} core modules — tap a
                  module to expand its topics.
                </p>
              </div>
              <ProgramHighlightsAccordion modules={program.highlights} />
            </div>
          ) : null}

          {program?.projects.length ? (
            <div className="mt-10">
              <h3 className="text-lg font-semibold text-gray-900">Projects</h3>
              <p className="mt-2 text-sm text-gray-600">
                {program.projects.length} real-world projects you build, explain, and add to your portfolio.
              </p>
              <div className="mt-6 grid md:grid-cols-3 gap-4">
                {program.projects.map((project) => (
                  <div key={project.title} className="surface-card p-5">
                    <h4 className="font-bold text-gray-900">{project.title}</h4>
                    {project.stack ? (
                      <p className="mt-2 text-xs font-semibold tracking-wide text-primary-800">
                        {project.stack.split(',').map((part) => part.trim()).join(' · ')}
                      </p>
                    ) : null}
                    {project.focus ? (
                      <p className="mt-3 text-sm text-gray-600 leading-relaxed">{project.focus}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {program?.outcomes.length ? (
            <div className="mt-10">
              <h3 className="text-lg font-semibold text-gray-900">What you leave with</h3>
              <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {program.outcomes.map((outcome) => (
                  <li key={outcome} className="flex gap-2.5 text-sm text-gray-700 leading-relaxed">
                    <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600" />
                    <span>{outcome}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {materials.length ? (
            <div className="mt-10">
              <h3 className="text-lg font-semibold text-gray-900">What’s included</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {materials.map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-slate-200/80 bg-white/80 px-3 py-1.5 text-xs font-medium text-gray-700"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {tools.length ? (
            <div className="mt-10">
              <h3 className="text-lg font-semibold text-gray-900">Tools</h3>
              <p className="mt-3 text-sm text-gray-600 leading-relaxed">{tools.join(', ')}</p>
            </div>
          ) : null}

          {programMentors.length > 0 ? (
            <div className="mt-10">
              <h3 className="text-lg font-semibold text-gray-900">Program mentors</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                Meet the industry mentors guiding this program. They’ll review your work, help you make sound engineering decisions, and share career insights from their experience.
              </p>
              <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {programMentors.map((mentor) => (
                  <div
                    key={mentor.mentorId}
                    className="surface-card p-5 flex gap-4"
                  >
                    <ProfileAvatar
                      src={mentor.profileImageUrl}
                      alt={mentor.mentorName || 'Mentor'}
                      className="h-14 w-14 rounded-full object-cover bg-gray-100 shrink-0"
                    />
                    <div className="min-w-0">
                      <h4 className="font-bold text-gray-900 truncate">{mentor.mentorName || 'Mentor'}</h4>
                      {mentor.jobTitle ? (
                        <p className="mt-0.5 text-sm text-primary-800 font-medium">{mentor.jobTitle}</p>
                      ) : null}
                      {mentor.functionalDomain ? (
                        <p className="text-xs text-gray-500 mt-0.5">{mentor.functionalDomain}</p>
                      ) : null}
                      {mentor.primaryExpertiseArea ? (
                        <p className="mt-2 text-sm text-gray-600 leading-relaxed line-clamp-3">
                          {mentor.primaryExpertiseArea}
                          {mentor.yearsOfExperience
                            ? ` · ${mentor.yearsOfExperience}+ yrs`
                            : ''}
                        </p>
                      ) : mentor.yearsOfExperience ? (
                        <p className="mt-2 text-sm text-gray-600">{mentor.yearsOfExperience}+ years experience</p>
                      ) : null}
                      {mentor.linkedInUrl ? (
                        <a
                          href={mentor.linkedInUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-2 inline-flex items-center gap-1 text-sm text-primary-600 hover:underline"
                        >
                          <Linkedin className="h-4 w-4" />
                          LinkedIn
                        </a>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className={heroCtaBand('academy')}>
        <div className="absolute -top-20 right-10 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16 text-center">
          <h2 className="text-2xl md:text-3xl font-bold">Ready to build your engineering career?</h2>
          <p className="mt-3 text-primary-100 max-w-2xl mx-auto leading-relaxed">
            Apply for <span className="font-semibold text-white">{course.title}</span>.
          </p>
          <div className="mt-7 flex flex-col items-center gap-3">
            {renderApplyActions({ variant: 'onDark' })}
            {enrollStatus && !isEnrolledForCourse && (
              <p className="text-sm text-primary-100">{enrollStatus}</p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgramDetail;
